import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  TextField,
  Alert,
  Divider,
  Paper,
  Chip,
  Stack,
  CircularProgress,
  Checkbox
} from '@mui/material';
import {
  Payment as PaymentIcon,
  CreditCard as CreditCardIcon,
  AccountBalance as BankIcon,
  Receipt as ReceiptIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckIcon,
  AttachMoney as MoneyIcon
} from '@mui/icons-material';
import { paymentAPI } from '../../services/api';

const PayInvoicePartial = ({ currentUser }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { order, invoiceDetails } = location.state || {};

  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [useNewPayment, setUseNewPayment] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  // Partial payment state
  const [paymentAmount, setPaymentAmount] = useState(invoiceDetails?.balance || 0);
  const [isPartialPayment, setIsPartialPayment] = useState(false);

  // New payment method form
  const [paymentType, setPaymentType] = useState('CREDIT_CARD');
  const [creditCardData, setCreditCardData] = useState({
    number: '',
    expiryMonth: '',
    expiryYear: '',
    cvv: '',
    cardholderName: ''
  });
  const [achData, setAchData] = useState({
    accountNumber: '',
    routingNumber: '',
    accountType: 'CHECKING',
    accountHolderName: ''
  });

  const customerId = currentUser?.email 
    ? `customer_${currentUser.email.replace('@', '_').replace('.', '_')}` 
    : 'customer_demo';

  useEffect(() => {
    if (!order) {
      navigate('/client/orders');
      return;
    }
    fetchPaymentMethods();
  }, [order]);

  const fetchPaymentMethods = async () => {
    try {
      // Temporarily disabled - no saved payment methods until proper Hyfin integration
      setPaymentMethods([]);
      setSelectedPaymentMethod('');
      setUseNewPayment(true);
    } catch (err) {
      console.error('Error fetching payment methods:', err);
      setPaymentMethods([]);
      setUseNewPayment(true);
    }
  };

  const getDiscountedAmount = (amount) => {
    // Only apply discount for FULL payments within 2 days
    const isFullPayment = amount >= invoiceDetails.balance;
    return (invoiceDetails?.earlyPaymentEligible && isFullPayment) ? amount - (amount * 0.1) : amount;
  };

  const isEligibleForDiscount = () => {
    return invoiceDetails?.earlyPaymentEligible && !isPartialPayment && paymentAmount >= invoiceDetails.balance;
  };

  const handlePaymentAmountChange = (value) => {
    // Ensure value doesn't exceed invoice balance
    const numValue = Math.min(Number(value) || 0, invoiceDetails.balance);
    setPaymentAmount(numValue);
    setIsPartialPayment(numValue < invoiceDetails.balance);
  };

  const handleProcessPayment = async () => {
    try {
      setProcessing(true);
      setError(null);

      // Calculate actual payment amount (with discount if applicable)
      const actualAmount = invoiceDetails.earlyPaymentEligible ? getDiscountedAmount(paymentAmount) : paymentAmount;

      // Always create payment method data (saved methods temporarily disabled)
      const paymentMethodData = {
        type: paymentType,
        ...(paymentType === 'CREDIT_CARD' ? {
          creditCard: {
            number: creditCardData.number.replace(/\s/g, ''),
            expiryMonth: creditCardData.expiryMonth,
            expiryYear: creditCardData.expiryYear,
            cvv: creditCardData.cvv,
            cardholderName: creditCardData.cardholderName
          }
        } : {
          ach: {
            accountNumber: achData.accountNumber,
            routingNumber: achData.routingNumber,
            accountType: achData.accountType,
            accountHolderName: achData.accountHolderName
          }
        })
      };

      const paymentData = {
        orderId: order.id,
        customerId: customerId, // Use consistent customer ID format
        customer: {
          firstName: order.shippingAddress?.name?.split(' ')[0] || order.clinic?.name || 'Dr.',
          lastName: order.shippingAddress?.name?.split(' ').slice(1).join(' ') || order.clinic?.name || 'Customer',
          email: order.clinic?.email || currentUser?.email || 'customer@example.com',
          ...(order.shippingAddress?.phone && { phone: order.shippingAddress.phone }),
          address: {
            street1: order.shippingAddress?.address1 || 'N/A',
            ...(order.shippingAddress?.address2 && { street2: order.shippingAddress.address2 }),
            city: order.shippingAddress?.city || 'N/A',
            state: order.shippingAddress?.state || 'N/A',
            zip: order.shippingAddress?.zipCode || '00000',
            country: 'US'
          }
        },
        paymentMethod: paymentMethodData, // Always use new payment method for now
        amount: actualAmount,
        currency: 'USD',
        description: isPartialPayment 
          ? `Partial payment for Order ${order.orderNumber || order.id}`
          : `Payment for Order ${order.orderNumber || order.id}`,
        savePaymentMethod: true // Always save for future (though we'll use new method each time for now)
      };

      console.log('Sending payment data:', paymentData);
      console.log('Using customer ID:', customerId);
      console.log('Selected payment method ID:', selectedPaymentMethod);
      const response = await paymentAPI.processInvoicePayment(paymentData);
      console.log('Payment response:', response);

      if (response.success) {
        navigate('/client/orders', {
          state: {
            message: isPartialPayment 
              ? `Partial payment of $${actualAmount.toFixed(2)} processed successfully!`
              : 'Payment processed successfully!',
            paidOrder: order
          }
        });
      } else {
        setError(response.error || 'Payment processing failed');
      }

    } catch (err) {
      console.error('Payment processing error:', err);
      setError(err.message || 'Payment processing failed');
    } finally {
      setProcessing(false);
    }
  };

  if (!order) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6">Order not found</Typography>
        <Button onClick={() => navigate('/client/orders')} sx={{ mt: 2 }}>
          Back to Orders
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, pt: { xs: 10, md: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
          Pay Invoice
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Complete payment for Order #{order.orderNumber || order.id} - Full or partial payment accepted
        </Typography>
      </Box>

      <Grid container spacing={4}>
        {/* Invoice Summary with Partial Payment Options */}
        <Grid item xs={12} md={5}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Invoice & Payment Amount
              </Typography>
              
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Invoice Total:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    ${invoiceDetails.totalAmount.toFixed(2)}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Amount Paid:</Typography>
                  <Typography variant="body2">
                    ${invoiceDetails.paidAmount.toFixed(2)}
                  </Typography>
                </Box>
                
                <Divider />
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>Balance Due:</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 700, color: 'error.main' }}>
                    ${invoiceDetails.balance.toFixed(2)}
                  </Typography>
                </Box>

                <Divider />

                {/* Payment Amount Selection */}
                <Box>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    Payment Amount
                  </Typography>
                  
                  <FormControl component="fieldset" fullWidth sx={{ mb: 2 }}>
                    <RadioGroup
                      value={isPartialPayment ? 'partial' : 'full'}
                      onChange={(e) => {
                        if (e.target.value === 'full') {
                          setPaymentAmount(invoiceDetails.balance);
                          setIsPartialPayment(false);
                        } else {
                          setIsPartialPayment(true);
                          setPaymentAmount(Math.min(invoiceDetails.balance / 2, 100)); // Default partial
                        }
                      }}
                    >
                      <FormControlLabel 
                        value="full" 
                        control={<Radio />} 
                        label={`Pay Full Amount ($${invoiceDetails.balance.toFixed(2)})`}
                      />
                      <FormControlLabel 
                        value="partial" 
                        control={<Radio />} 
                        label="Make Partial Payment"
                      />
                    </RadioGroup>
                  </FormControl>

                  {/* Partial Payment Amount Input */}
                  {isPartialPayment && (
                    <Box sx={{ mb: 2 }}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Payment Amount"
                        value={paymentAmount}
                        onChange={(e) => handlePaymentAmountChange(e.target.value)}
                        inputProps={{ 
                          min: 50, 
                          max: invoiceDetails.balance, 
                          step: 0.01 
                        }}
                        helperText={`Enter amount between $50.00 and $${invoiceDetails.balance.toFixed(2)} (full balance)`}
                        sx={{ 
                          '& .MuiOutlinedInput-root': {
                            fontSize: '1.1rem'
                          }
                        }}
                        InputProps={{
                          startAdornment: <Typography sx={{ mr: 1, color: 'text.secondary' }}>$</Typography>
                        }}
                      />
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                        💡 <strong>Tip:</strong> Pay the full amount (${invoiceDetails.balance.toFixed(2)}) to receive 10% discount!
                      </Typography>
                    </Box>
                  )}
                </Box>

                {invoiceDetails.earlyPaymentEligible && (
                  isEligibleForDiscount() ? (
                    <Alert severity="success">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        10% Early Payment Discount Applied!
                      </Typography>
                      <Typography variant="body2">
                        You'll pay ${getDiscountedAmount(paymentAmount).toFixed(2)} instead of ${paymentAmount.toFixed(2)}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                        Total savings: ${(paymentAmount - getDiscountedAmount(paymentAmount)).toFixed(2)}
                      </Typography>
                    </Alert>
                  ) : (
                    <Alert severity="info">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        10% Early Payment Discount Available
                      </Typography>
                      <Typography variant="body2">
                        {isPartialPayment 
                          ? "Discount only applies to full invoice payments. Pay the full amount to receive 10% off!"
                          : "Pay the full invoice amount within 2 days to receive 10% discount."
                        }
                      </Typography>
                    </Alert>
                  )
                )}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2, p: 2, bgcolor: '#f8fafc', borderRadius: 1 }}>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>You'll Pay:</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: isEligibleForDiscount() ? 'success.main' : 'primary.main' }}>
                    ${getDiscountedAmount(paymentAmount).toFixed(2)}
                    {isEligibleForDiscount() && (
                      <Typography variant="body2" component="span" sx={{ color: 'success.main', ml: 1 }}>
                        (10% discount applied)
                      </Typography>
                    )}
                  </Typography>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 2 }}>
                  {(() => {
                    const baseDate = new Date(order?.orderDate || order?.createdAt || Date.now());
                    const net60 = invoiceDetails?.net60Date
                      ? new Date(invoiceDetails.net60Date)
                      : new Date(baseDate.getTime() + 60 * 24 * 60 * 60 * 1000);
                    return `Net 60 Due Date: ${net60.toLocaleDateString()}`;
                  })()}
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Payment Method Selection - Same as PayInvoice */}
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Select Payment Method
              </Typography>

              {/* Temporarily hide saved payment methods until Hyfin integration is complete */}
              <Typography variant="body2" color="info.main" sx={{ mb: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                Note: Using new payment method for secure processing. Payment methods will be stored in Hyfin for future use.
              </Typography>

              {/* New Payment Method Form - Always show */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>New Payment Method</Typography>
                  
                  <FormControl component="fieldset" fullWidth sx={{ mb: 3 }}>
                    <FormLabel component="legend">Payment Type</FormLabel>
                    <RadioGroup
                      value={paymentType}
                      onChange={(e) => setPaymentType(e.target.value)}
                      row
                    >
                      <FormControlLabel value="CREDIT_CARD" control={<Radio />} label="Credit Card" />
                      <FormControlLabel value="ACH" control={<Radio />} label="Bank Account" />
                    </RadioGroup>
                  </FormControl>

                  {paymentType === 'CREDIT_CARD' ? (
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Cardholder Name"
                          value={creditCardData.cardholderName}
                          onChange={(e) => setCreditCardData({...creditCardData, cardholderName: e.target.value})}
                          required
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Card Number"
                          value={creditCardData.number}
                          onChange={(e) => setCreditCardData({...creditCardData, number: e.target.value})}
                          placeholder="1234 5678 9012 3456"
                          required
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="Expiry Month"
                          value={creditCardData.expiryMonth}
                          onChange={(e) => setCreditCardData({...creditCardData, expiryMonth: e.target.value})}
                          placeholder="MM"
                          inputProps={{ maxLength: 2 }}
                          required
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="Expiry Year"
                          value={creditCardData.expiryYear}
                          onChange={(e) => setCreditCardData({...creditCardData, expiryYear: e.target.value})}
                          placeholder="YYYY"
                          inputProps={{ maxLength: 4 }}
                          required
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="CVV"
                          value={creditCardData.cvv}
                          onChange={(e) => setCreditCardData({...creditCardData, cvv: e.target.value})}
                          placeholder="123"
                          inputProps={{ maxLength: 4 }}
                          required
                        />
                      </Grid>
                    </Grid>
                  ) : (
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Account Holder Name"
                          value={achData.accountHolderName}
                          onChange={(e) => setAchData({...achData, accountHolderName: e.target.value})}
                          required
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Routing Number"
                          value={achData.routingNumber}
                          onChange={(e) => setAchData({...achData, routingNumber: e.target.value})}
                          placeholder="123456789"
                          required
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Account Number"
                          value={achData.accountNumber}
                          onChange={(e) => setAchData({...achData, accountNumber: e.target.value})}
                          required
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormControl fullWidth>
                          <FormLabel component="legend">Account Type</FormLabel>
                          <RadioGroup
                            value={achData.accountType}
                            onChange={(e) => setAchData({...achData, accountType: e.target.value})}
                            row
                          >
                            <FormControlLabel value="CHECKING" control={<Radio />} label="Checking" />
                            <FormControlLabel value="SAVINGS" control={<Radio />} label="Savings" />
                          </RadioGroup>
                        </FormControl>
                      </Grid>
                    </Grid>
                  )}
                </Box>
              

              {error && (
                <Alert severity="error" sx={{ mt: 3 }}>
                  {error}
                </Alert>
              )}

              <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/client/orders')}
                  disabled={processing}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleProcessPayment}
                  disabled={processing || (!selectedPaymentMethod && !useNewPayment)}
                  startIcon={processing ? <CircularProgress size={20} /> : <PaymentIcon />}
                  sx={{
                    bgcolor: '#0d9488',
                    '&:hover': { bgcolor: '#0f766e' },
                    px: 4,
                    py: 1.5,
                    fontWeight: 600,
                    flex: 1
                  }}
                >
                  {processing ? 'Processing...' : 
                   `${isPartialPayment ? 'Pay Partial' : 'Pay Full'} ${
                     isEligibleForDiscount()
                       ? `$${getDiscountedAmount(paymentAmount).toFixed(2)} (Save 10%)`
                       : `$${paymentAmount.toFixed(2)}`
                   }`
                  }
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PayInvoicePartial;
